import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Bell, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vr-campus-refresh/artifacts/2qtl4jp2_303B42DC-0FAA-485C-9E1C-89CDB95BC84D.jpeg";

// Partner logos
const VICTORYXR_LOGO = "https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/h02r640c_IMG_4656.jpeg";
const META_LOGO = "https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/4l2csir8_IMG_4658.jpeg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComingSoonPage() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/subscribers`, {
        email: email,
        source: 'coming_soon'
      });
      setIsSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Error subscribing:', err);
      // Still show success even if already subscribed
      setIsSubmitted(true);
      setEmail('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-black" data-testid="coming-soon-page">
      
      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 text-center">
        {/* Logo - Large without circle */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <img
            src={LOGO_URL}
            alt="The Matrix University"
            className="w-40 h-40 md:w-56 md:h-56 mx-auto"
          />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-4xl md:text-6xl font-serif font-bold text-white mb-4"
        >
          Coming Soon
        </motion.h1>

        {/* Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-2"
        >
          <h2 className="text-2xl md:text-3xl font-serif text-primary">
            The Matrix University
          </h2>
          <p className="text-lg text-secondary mt-1">
            Dr. Jason E. Williams, Ed.D.
          </p>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg md:text-xl text-gray-300 mb-8 max-w-lg mx-auto"
        >
          We're preparing something extraordinary. Immersive VR education that will transform how you learn.
        </motion.p>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-cta font-semibold text-lg mb-10"
        >
          Where Education Meets the Future
        </motion.p>

        {/* Email Signup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-12"
        >
          {isSubmitted ? (
            <div className="p-6 rounded-2xl bg-white/10 inline-flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <span className="text-white font-medium">Thank you! We'll notify you when we launch.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="bg-white/10 border-white/20 h-12 text-center sm:text-left text-white placeholder:text-gray-400"
                  data-testid="coming-soon-email"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary h-12 px-6"
                data-testid="coming-soon-submit"
              >
                {isSubmitting ? (
                  'Subscribing...'
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" />
                    Notify Me
                  </>
                )}
              </Button>
            </form>
          )}
        </motion.div>

        {/* Features Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12"
        >
          {[
            { icon: '🎓', title: 'VR Courses', desc: 'Immersive learning' },
            { icon: '🌍', title: 'Virtual Campus', desc: 'Explore in VR' },
            { icon: '📚', title: 'Digital Library', desc: 'Books & resources' },
          ].map((item, i) => (
            <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-3xl mb-2">{item.icon}</div>
              <h3 className="text-white font-semibold">{item.title}</h3>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Partners */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-8"
        >
          <p className="text-gray-500 text-sm mb-4">Our Partners</p>
          <div className="flex items-center justify-center gap-6">
            <img 
              src={VICTORYXR_LOGO} 
              alt="VictoryXR" 
              className="h-12 md:h-16 w-auto rounded-lg opacity-80 hover:opacity-100 transition-opacity"
            />
            <img 
              src={META_LOGO} 
              alt="Meta" 
              className="h-12 md:h-16 w-auto rounded-lg opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="text-gray-500 text-sm px-4 mb-16"
        >
          <p className="flex flex-col items-center justify-center gap-2 text-center">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Questions?
            </span>
            <a href="mailto:DrJason@thematrixuniversity.com" className="text-primary hover:underline">
              DrJason@thematrixuniversity.com
            </a>
          </p>
        </motion.div>
      </div>

      {/* Footer - More padding from bottom */}
      <div className="absolute bottom-8 left-0 right-0 text-center px-4">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} The Matrix University. All rights reserved.
        </p>
      </div>
    </div>
  );
}
